/**
 * Veltro vehicle memory.
 *
 * One tiny store for "which BYD does this shopper drive". Written by the hero
 * selector, the header pill and the PDP fitment picker; read by every "Fits
 * your car" line, the model-filtered upsells in the cart drawer and the
 * collection links. Persisted in localStorage AND mirrored to a cart attribute
 * so the order itself carries the car (packing slips, support, analytics).
 *
 * Model handles are the same strings used in product tags: `fits:atto-3`.
 */
(() => {
  const KEY = 'veltro:vehicle';
  const EVENT = 'veltro:vehicle:change';

  const read = () => {
    try {
      const raw = localStorage.getItem(KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (_) {
      return null;
    }
  };

  const write = (vehicle) => {
    try {
      if (vehicle) localStorage.setItem(KEY, JSON.stringify(vehicle));
      else localStorage.removeItem(KEY);
    } catch (_) {}
    document.documentElement.dataset.veltroVehicle = vehicle ? vehicle.handle : '';
    document.dispatchEvent(new CustomEvent(EVENT, { detail: vehicle }));
    syncCartAttribute(vehicle);
  };

  let cartSyncTimer = null;
  const syncCartAttribute = (vehicle) => {
    clearTimeout(cartSyncTimer);
    cartSyncTimer = setTimeout(() => {
      const root = (window.Theme && Theme.routes && Theme.routes.root) || '/';
      fetch(`${root.replace(/\/$/, '')}/cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { 'BYD model': vehicle ? vehicle.label : '' } }),
      }).catch(() => {});
    }, 400);
  };

  /** Every element that shows the chosen car reads from here. */
  const paint = (vehicle) => {
    document.querySelectorAll('[data-veltro-vehicle-label]').forEach((el) => {
      el.textContent = vehicle ? vehicle.label : el.dataset.veltroEmpty || 'Select your BYD';
    });
    document.querySelectorAll('[data-veltro-vehicle-state]').forEach((el) => {
      el.dataset.veltroVehicleState = vehicle ? 'set' : 'empty';
    });
    document.querySelectorAll('[data-veltro-model-option]').forEach((el) => {
      const on = !!vehicle && el.dataset.veltroModelOption === vehicle.handle;
      el.setAttribute('aria-pressed', on ? 'true' : 'false');
      el.classList.toggle('is-active', on);
    });
    document.querySelectorAll('select[data-veltro-vehicle-select]').forEach((sel) => {
      sel.value = vehicle ? vehicle.handle : '';
    });
    // Fitment lines: an element declares which handles it fits with data-veltro-fits="atto-3,seal"
    document.querySelectorAll('[data-veltro-fits]').forEach((el) => {
      const fits = el.dataset.veltroFits.split(',').map((s) => s.trim()).filter(Boolean);
      let state = 'unknown';
      if (vehicle) state = fits.includes(vehicle.handle) ? 'fits' : fits.length ? 'no-fit' : 'unknown';
      el.dataset.veltroFitState = state;
    });
    // Model-scoped links: href swaps to the chosen model's collection when known.
    document.querySelectorAll('a[data-veltro-model-link]').forEach((a) => {
      const base = a.dataset.veltroModelLink; // e.g. "/collections/{model}" or "/collections/{model}-floor-mats"
      if (vehicle) a.href = base.replace('[model]', vehicle.handle).replace('{model}', vehicle.handle);
      else a.href = a.dataset.veltroModelFallback || '/collections/all';
    });
  };

  window.VeltroVehicle = {
    get: read,
    /** Re-apply the saved car to freshly rendered DOM (e.g. after Horizon morphs a section). */
    paint() {
      paint(read());
    },
    set(handle, label) {
      const vehicle = handle ? { handle, label: label || handle } : null;
      write(vehicle);
      paint(vehicle);
    },
    clear() {
      write(null);
      paint(null);
    },
    EVENT,
  };

  // Delegated handlers: buttons and selects anywhere on the page.
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-veltro-model-option]');
    if (!btn) return;
    const current = read();
    const handle = btn.dataset.veltroModelOption;
    const label = btn.dataset.veltroModelLabel || btn.textContent.trim();
    if (current && current.handle === handle && btn.hasAttribute('data-veltro-toggle')) {
      window.VeltroVehicle.clear();
    } else {
      window.VeltroVehicle.set(handle, label);
    }
    const go = btn.dataset.veltroModelGo; // optional: navigate after choosing
    if (go) window.location.href = go.replace('[model]', handle).replace('{model}', handle);
  });

  document.addEventListener('change', (e) => {
    const sel = e.target.closest('select[data-veltro-vehicle-select]');
    if (!sel) return;
    const opt = sel.selectedOptions[0];
    if (!sel.value) window.VeltroVehicle.clear();
    else window.VeltroVehicle.set(sel.value, opt ? opt.textContent.trim() : sel.value);
  });

  const init = () => paint(read());
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  // Sections re-rendered by the theme editor or the Section Rendering API need repainting.
  document.addEventListener('shopify:section:load', init);
  new MutationObserver(() => paint(read())).observe(document.body || document.documentElement, { childList: true, subtree: false });
})();
